import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InboxIcon from "@mui/icons-material/Inbox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import { KnowledgeFolder } from "../../models/API";

/** Returns icon color based on folder visibility */
function getFolderColor(folder: KnowledgeFolder): string {
  if (folder.is_global) return "#ed6c02"; // orange
  if (folder.is_public) return "#2e7d32"; // green
  return "#1565c0"; // blue
}

interface MoveToFolderDialogProps {
  open: boolean;
  folders: KnowledgeFolder[];
  currentFolderId: number | null;
  onClose: () => void;
  onMove: (folderId: number | null) => void;
}

export default function MoveToFolderDialog({
  open,
  folders,
  currentFolderId,
  onClose,
  onMove,
}: MoveToFolderDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: "primary.50",
            }}
          >
            <DriveFileMoveIcon sx={{ color: "primary.main", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Move to Folder
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select a destination folder
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List dense sx={{ px: 1 }}>
          <ListItemButton
            selected={currentFolderId === null}
            onClick={() => onMove(null)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              mx: 0.5,
              transition: "all 0.15s ease",
              "&.Mui-selected": {
                backgroundColor: "primary.50",
                "&:hover": { backgroundColor: "primary.100" },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <InboxIcon
                fontSize="small"
                color={currentFolderId === null ? "primary" : "action"}
              />
            </ListItemIcon>
            <ListItemText
              primary="Root Level (No Folder)"
              primaryTypographyProps={{
                fontWeight: currentFolderId === null ? 600 : 400,
                variant: "body2",
              }}
            />
            {currentFolderId === null && (
              <CheckCircleIcon sx={{ fontSize: 18, color: "primary.main" }} />
            )}
          </ListItemButton>
          {folders.map((folder) => {
            const isCurrent = currentFolderId === folder.id;
            const iconColor = getFolderColor(folder);
            return (
              <ListItemButton
                key={folder.id}
                selected={isCurrent}
                onClick={() => onMove(folder.id)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  mx: 0.5,
                  transition: "all 0.15s ease",
                  "&.Mui-selected": {
                    backgroundColor: "primary.50",
                    "&:hover": { backgroundColor: "primary.100" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderIcon sx={{ fontSize: 20, color: iconColor }} />
                </ListItemIcon>
                <ListItemText
                  primary={folder.folder_name}
                  primaryTypographyProps={{
                    fontWeight: isCurrent ? 600 : 400,
                    variant: "body2",
                  }}
                />
                {isCurrent && (
                  <CheckCircleIcon
                    sx={{ fontSize: 18, color: "primary.main" }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
